import React from 'react';
import { Box, Container, Link, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { mergeWith } from 'lodash';
import {
  AlternateEmail as EmailIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Language as LanguageIcon,
  LinkedIn as LinkedInIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';

const ICON_SIZE = 40;

type SocialMediaData = {
  title: string;
  icon: JSX.Element;
  url?: string;
};

const customizer = (objValue: SocialMediaData, srcValue: string): SocialMediaData => ({
  ...objValue,
  url: srcValue,
});

function Footer({ space }: any) {
  const intl = useIntl();

  const socialsMetadata: Record<string, SocialMediaData> = {
    website_url: {
      title: 'website',
      icon: <LanguageIcon style={{ fontSize: 24 }} />,
    },
    email: {
      title: 'email',
      icon: <EmailIcon style={{ fontSize: 24 }} />,
    },
    facebook_url: {
      title: 'facebook',
      icon: <FacebookIcon style={{ fontSize: 26 }} />,
    },
    twitter_url: {
      title: 'twitter',
      icon: <XIcon style={{ fontSize: 22 }} />,
    },
    linkedin_url: {
      title: 'linkedin',
      icon: <LinkedInIcon style={{ fontSize: 26 }} />,
    },
    instagram_url: {
      title: 'instagram',
      icon: <InstagramIcon style={{ fontSize: 26 }} />,
    },
  };

  const mapedData = mergeWith(socialsMetadata, space.social_media_urls, customizer) as Record<
    string,
    SocialMediaData
  >;

  const socialMediaArray = Object.values(mapedData).filter((social) => social.url);
  const spaceAddress = space.address;

  const generateLink = (social: any) => {
    if (social.title === 'email') return `mailto:${social.url}`;

    return social.url;
  };

  if (!socialMediaArray.length && !space.phone_number && !spaceAddress) {
    return null;
  }

  return (
    <Box
      sx={{
        color: 'white',
        backgroundColor: 'primary.main',
      }}
    >
      <Container maxWidth="md" sx={{ paddingX: 3, paddingY: 7, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 'bold',
            display: 'inline-block',
            marginBottom: 3,
          }}
        >
          <FormattedMessage id="SPACE.HOME.FOOTER.TITLE" />
        </Typography>

        {(space.phone_number || spaceAddress) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            {space.phone_number && (
              <Box
                sx={{
                  display: 'flex',
                  mr: 3,
                }}
              >
                <Box aria-hidden="true" sx={{ mr: 1 }}>
                  <PhoneIcon style={{ fontSize: 24 }} />
                </Box>
                <Typography variant="body2" component="p">
                  {space.phone_number}
                </Typography>
              </Box>
            )}

            {spaceAddress && (
              <Box sx={{ display: 'flex' }}>
                <Box aria-hidden="true" sx={{ mr: 1 }}>
                  <LocationOnIcon style={{ fontSize: 24 }} />
                </Box>
                <Typography variant="body2" component="p">
                  {spaceAddress}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridGap: (theme: any) => theme.spacing(3),
            gridTemplateColumns: `repeat(auto-fit, ${ICON_SIZE}px)`,
            gridAutoRows: ICON_SIZE,
            justifyContent: 'center',
          }}
        >
          {socialMediaArray.map((media) => (
            <Box
              sx={{
                width: ICON_SIZE,
                height: ICON_SIZE,
                borderRadius: '50%',
                backgroundColor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& a': {
                  display: 'flex',
                },
              }}
              key={media.title}
            >
              <Link
                href={generateLink(media)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={media.title}
                underline="hover"
                title={intl.formatMessage({ id: 'BUTTONS.OPEN_IN_NEW_TAB' })}
              >
                {media.icon}
              </Link>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
